import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  BadgeCheck,
  BellRing,
  CalendarClock,
  Pencil,
  Plus,
  ShieldCheck,
  Upload,
} from "lucide-react";
import {
  getFacilities,
  getFacilityFeatureFlags,
} from "../../lib/services/facilities";
import {
  createCredentialingProvider,
  emitCredentialingExpiryAlerts,
  getCredentialingDocuments,
  getCredentialingProviderDetail,
  getCredentialingProviders,
  getCredentialingSummary,
  updateCredentialingProvider,
  uploadCredentialDocument,
  type CredentialDocumentType,
  type CredentialRecord,
  type ExpiryStatus,
  type PrivilegeRecord,
  type ProviderStatus,
} from "../../lib/services/credentialing";

const PROVIDER_STATUS_OPTIONS: ProviderStatus[] = [
  "active",
  "provisional",
  "inactive",
];

const EXPIRY_STYLES: Record<ExpiryStatus, string> = {
  green: "bg-emerald-50 text-emerald-700",
  amber: "bg-amber-50 text-amber-700",
  red: "bg-rose-50 text-rose-700",
  unknown: "bg-slate-100 text-slate-700",
};

function formatDate(value: string | null): string {
  if (!value) return "Not scheduled";
  const parsed = new Date(value);
  return Number.isNaN(parsed.valueOf()) ? value : parsed.toLocaleDateString();
}

function formatCredentialType(value: string): string {
  return value
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function buildTimeline(
  credentials: CredentialRecord[],
  privileges: PrivilegeRecord[],
) {
  return [
    ...credentials.map((credential) => ({
      id: credential.credential_id,
      kind: "Credential",
      label: formatCredentialType(credential.credential_type),
      expiry_date: credential.expiry_date,
    })),
    ...privileges.map((privilege) => ({
      id: privilege.privilege_id,
      kind: "Privilege",
      label: formatCredentialType(privilege.procedure_category),
      expiry_date: privilege.expiry_date,
    })),
  ]
    .filter((entry) => entry.expiry_date)
    .sort((a, b) => String(a.expiry_date).localeCompare(String(b.expiry_date)));
}

export function CredentialingWorkspacePage() {
  const queryClient = useQueryClient();
  const [selectedFacility, setSelectedFacility] = useState("");
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(
    null,
  );
  const [uploadLabel, setUploadLabel] = useState("");
  const [uploadType, setUploadType] = useState<CredentialDocumentType | "other">(
    "medical_license",
  );
  const [uploadCredentialId, setUploadCredentialId] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createNpi, setCreateNpi] = useState("");
  const [createSpecialty, setCreateSpecialty] = useState("");
  const [createStatus, setCreateStatus] = useState<ProviderStatus>("active");
  const [createFacilityId, setCreateFacilityId] = useState("");
  const [editName, setEditName] = useState("");
  const [editNpi, setEditNpi] = useState("");
  const [editSpecialty, setEditSpecialty] = useState("");
  const [editStatus, setEditStatus] = useState<ProviderStatus>("active");
  const [editFacilityId, setEditFacilityId] = useState("");

  const featureFlagsQuery = useQuery({
    queryKey: ["credentialing-feature-flags"],
    queryFn: () => getFacilityFeatureFlags(),
  });
  const facilitiesQuery = useQuery({
    queryKey: ["credentialing-facilities"],
    queryFn: getFacilities,
  });
  const summaryQuery = useQuery({
    queryKey: ["credentialing-summary", selectedFacility],
    queryFn: () => getCredentialingSummary(selectedFacility || undefined),
  });
  const providersQuery = useQuery({
    queryKey: ["credentialing-providers", selectedFacility],
    queryFn: () => getCredentialingProviders(selectedFacility || undefined),
  });

  const providers = useMemo(
    () => providersQuery.data ?? [],
    [providersQuery.data],
  );
  const activeProviderId =
    selectedProviderId ?? providers[0]?.provider_id ?? null;

  const providerDetailQuery = useQuery({
    queryKey: ["credentialing-provider-detail", activeProviderId],
    queryFn: () => getCredentialingProviderDetail(activeProviderId as string),
    enabled: activeProviderId !== null,
  });
  const documentsQuery = useQuery({
    queryKey: ["credentialing-documents", activeProviderId],
    queryFn: () => getCredentialingDocuments(activeProviderId as string),
    enabled: activeProviderId !== null,
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!activeProviderId || !uploadLabel.trim() || !selectedFile) {
        throw new Error("Document type, label, and file are required.");
      }
      return await uploadCredentialDocument(activeProviderId, {
        document_type: uploadType,
        label: uploadLabel.trim(),
        credential_id: uploadCredentialId || undefined,
        file: selectedFile,
      });
    },
    onSuccess: async () => {
      setUploadLabel("");
      setUploadCredentialId("");
      setSelectedFile(null);
      setFeedbackMessage("Credential document uploaded.");
      await queryClient.invalidateQueries({
        queryKey: ["credentialing-documents", activeProviderId],
      });
    },
  });

  const alertMutation = useMutation({
    mutationFn: async () => {
      if (!activeProviderId) throw new Error("Select a provider first.");
      return await emitCredentialingExpiryAlerts(activeProviderId);
    },
    onSuccess: (result) => {
      setFeedbackMessage(
        result.count > 0
          ? `Sent ${result.count} credentialing alert${result.count === 1 ? "" : "s"}.`
          : "No expiring credentialing items needed alerts.",
      );
    },
  });

  const createProviderMutation = useMutation({
    mutationFn: async () => {
      const facilityId =
        createFacilityId ||
        selectedFacility ||
        facilitiesQuery.data?.[0]?.facility_id ||
        "";
      if (!facilityId) throw new Error("Select a facility for the new provider.");
      if (!createName.trim() || !createSpecialty.trim()) {
        throw new Error("Name and specialty are required.");
      }
      if (!/^\d{10}$/.test(createNpi.trim())) {
        throw new Error("NPI must be a 10-digit value.");
      }
      return await createCredentialingProvider({
        facility_id: facilityId,
        name: createName.trim(),
        npi: createNpi.trim(),
        specialty: createSpecialty.trim(),
        status: createStatus,
      });
    },
    onSuccess: async (provider) => {
      setFormError(null);
      setShowCreateForm(false);
      setCreateName("");
      setCreateNpi("");
      setCreateSpecialty("");
      setCreateStatus("active");
      setCreateFacilityId("");
      setSelectedProviderId(provider.provider_id);
      setFeedbackMessage(`Provider ${provider.name} created.`);
      await queryClient.invalidateQueries({ queryKey: ["credentialing-providers"] });
      await queryClient.invalidateQueries({ queryKey: ["credentialing-summary"] });
    },
    onError: (err) => {
      setFormError(err instanceof Error ? err.message : "Failed to create provider");
    },
  });

  const updateProviderMutation = useMutation({
    mutationFn: async () => {
      if (!activeProviderId) throw new Error("Select a provider first.");
      if (!editName.trim() || !editSpecialty.trim()) {
        throw new Error("Name and specialty are required.");
      }
      if (!/^\d{10}$/.test(editNpi.trim())) {
        throw new Error("NPI must be a 10-digit value.");
      }
      return await updateCredentialingProvider(activeProviderId, {
        facility_id: editFacilityId || undefined,
        name: editName.trim(),
        npi: editNpi.trim(),
        specialty: editSpecialty.trim(),
        status: editStatus,
      });
    },
    onSuccess: async (provider) => {
      setFormError(null);
      setShowEditForm(false);
      setFeedbackMessage(`Provider ${provider.name} updated.`);
      await queryClient.invalidateQueries({ queryKey: ["credentialing-providers"] });
      await queryClient.invalidateQueries({
        queryKey: ["credentialing-provider-detail", activeProviderId],
      });
      await queryClient.invalidateQueries({ queryKey: ["credentialing-summary"] });
    },
    onError: (err) => {
      setFormError(err instanceof Error ? err.message : "Failed to update provider");
    },
  });

  const credentialingEnabled = useMemo(
    () =>
      (featureFlagsQuery.data ?? []).some(
        (flag) => flag.feature_key === "credentialing" && flag.enabled,
      ),
    [featureFlagsQuery.data],
  );

  if (!credentialingEnabled && featureFlagsQuery.isSuccess) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
        Credentialing is not enabled for this facility.
      </div>
    );
  }

  const summary = summaryQuery.data;
  const detail = providerDetailQuery.data;
  const documents = documentsQuery.data ?? [];
  const timeline = buildTimeline(
    detail?.credentials ?? [],
    detail?.privileges ?? [],
  );
  const activeFacilityName =
    facilitiesQuery.data?.find(
      (facility) =>
        facility.facility_id ===
        (detail?.provider.facility_id ?? selectedFacility),
    )?.facility_name ?? "Selected facility";

  return (
    <div className="space-y-6">
      <header className="v11-workspace-header">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-[var(--brand-secondary)]">
              Facility operations
            </p>
            <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Credentialing</h1>
            <p className="mt-1 text-sm text-slate-600">
              Provider credential and privileging workspace built on the bounded
              Sprint 1 and Sprint 2 surfaces. Sprint 3 adds document handling
              and manual expiration-alert follow-ons without opening external
              verification scope.
            </p>
          </div>
          {facilitiesQuery.data && facilitiesQuery.data.length > 1 && (
            <select
              value={selectedFacility}
              onChange={(event) => {
                setSelectedFacility(event.target.value);
                setSelectedProviderId(null);
                setFeedbackMessage(null);
              }}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
            >
              <option value="">All Facilities</option>
              {facilitiesQuery.data.map((facility) => (
                <option key={facility.facility_id} value={facility.facility_id}>
                  {facility.facility_name}
                </option>
              ))}
            </select>
          )}
        </div>

        {summary && (
          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <div className="v11-kpi-card">
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.24em] text-slate-500">
                <ShieldCheck size={14} /> Providers
              </div>
              <div className="mt-3 text-3xl font-black text-slate-900">
                {summary.total_providers}
              </div>
            </div>
            <div className="v11-kpi-card v11-kpi-accent-rose">
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.24em] text-rose-700">
                <AlertTriangle size={14} /> Expired
              </div>
              <div className="mt-3 text-3xl font-black text-rose-700">
                {summary.status_counts.red}
              </div>
            </div>
            <div className="v11-kpi-card v11-kpi-accent-gold">
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.24em] text-amber-700">
                <CalendarClock size={14} /> Expiring Soon
              </div>
              <div className="mt-3 text-3xl font-black text-amber-700">
                {summary.status_counts.amber}
              </div>
            </div>
            <div className="v11-kpi-card v11-kpi-accent-teal">
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.24em] text-emerald-700">
                <BadgeCheck size={14} /> Current
              </div>
              <div className="mt-3 text-3xl font-black text-emerald-700">
                {summary.status_counts.green}
              </div>
            </div>
          </div>
        )}

        {feedbackMessage && (
          <div className="mt-4 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-medium text-sky-700" role="status">
            {feedbackMessage}
          </div>
        )}
        {formError && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700" role="alert">
            {formError}
          </div>
        )}
      </header>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.25fr)]">
        <section className="v11-section p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">
                Provider Roster
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Select a provider to inspect credentials, privileges,
                reappointment timing, supporting files, and bounded alert
                follow-ons. Admins can create and edit providers (#1852).
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowCreateForm((open) => !open);
                setFormError(null);
                if (!createFacilityId && selectedFacility) {
                  setCreateFacilityId(selectedFacility);
                } else if (!createFacilityId && facilitiesQuery.data?.[0]) {
                  setCreateFacilityId(facilitiesQuery.data[0].facility_id);
                }
              }}
              className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-[var(--brand-primary)] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
            >
              <Plus size={16} />
              {showCreateForm ? "Cancel" : "Add provider"}
            </button>
          </div>

          {showCreateForm && (
            <form
              className="mt-4 space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4"
              onSubmit={(event) => {
                event.preventDefault();
                setFormError(null);
                createProviderMutation.mutate();
              }}
            >
              <p className="text-xs font-black uppercase tracking-widest text-slate-500">
                New provider
              </p>
              <label className="block text-xs font-semibold text-slate-600">
                Facility
                <select
                  value={createFacilityId || selectedFacility || facilitiesQuery.data?.[0]?.facility_id || ""}
                  onChange={(event) => setCreateFacilityId(event.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800"
                  required
                >
                  <option value="" disabled>
                    Select facility
                  </option>
                  {(facilitiesQuery.data ?? []).map((facility) => (
                    <option key={facility.facility_id} value={facility.facility_id}>
                      {facility.facility_name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs font-semibold text-slate-600">
                Name
                <input
                  value={createName}
                  onChange={(event) => setCreateName(event.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800"
                  placeholder="Dr. Jane Smith"
                  required
                />
              </label>
              <label className="block text-xs font-semibold text-slate-600">
                NPI (10 digits)
                <input
                  value={createNpi}
                  onChange={(event) => setCreateNpi(event.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800"
                  placeholder="1234567890"
                  inputMode="numeric"
                  pattern="\d{10}"
                  required
                />
              </label>
              <label className="block text-xs font-semibold text-slate-600">
                Specialty
                <input
                  value={createSpecialty}
                  onChange={(event) => setCreateSpecialty(event.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800"
                  placeholder="Anesthesia"
                  required
                />
              </label>
              <label className="block text-xs font-semibold text-slate-600">
                Status
                <select
                  value={createStatus}
                  onChange={(event) => setCreateStatus(event.target.value as ProviderStatus)}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800"
                >
                  {PROVIDER_STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="submit"
                disabled={createProviderMutation.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {createProviderMutation.isPending ? "Creating…" : "Create provider"}
              </button>
            </form>
          )}

          <div className="mt-4 space-y-3">
            {providers.map((provider) => (
              <button
                key={provider.provider_id}
                type="button"
                onClick={() => {
                  setSelectedProviderId(provider.provider_id);
                  setFeedbackMessage(null);
                  setFormError(null);
                  // #1852 G1 M1: never leave Edit form seeded from a prior provider.
                  setShowEditForm(false);
                }}
                className={`w-full rounded-2xl border px-4 py-4 text-left transition ${
                  provider.provider_id === activeProviderId
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white hover:border-[var(--brand-primary)] hover:shadow-sm"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-base font-bold">{provider.name}</p>
                    <p
                      className={`mt-1 text-sm ${provider.provider_id === activeProviderId ? "text-slate-300" : "text-slate-500"}`}
                    >
                      {provider.specialty} • NPI {provider.npi}
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-2.5 py-1 text-xs font-bold ${provider.provider_id === activeProviderId ? "bg-white/15 text-white" : EXPIRY_STYLES[provider.summary.provider_status]}`}
                  >
                    {provider.summary.provider_status}
                  </span>
                </div>
                <div
                  className={`mt-3 flex flex-wrap gap-2 text-xs font-semibold ${provider.provider_id === activeProviderId ? "text-slate-200" : "text-slate-600"}`}
                >
                  <span>
                    Credentials:{" "}
                    {provider.summary.credential_status_counts.green +
                      provider.summary.credential_status_counts.amber +
                      provider.summary.credential_status_counts.red +
                      provider.summary.credential_status_counts.unknown}
                  </span>
                  <span>
                    Privileges:{" "}
                    {provider.summary.privilege_status_counts.green +
                      provider.summary.privilege_status_counts.amber +
                      provider.summary.privilege_status_counts.red +
                      provider.summary.privilege_status_counts.unknown}
                  </span>
                  <span>
                    Next expiry: {formatDate(provider.summary.next_expiry_date)}
                  </span>
                </div>
              </button>
            ))}
            {!providersQuery.isLoading && providers.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">
                No providers found for this facility. Use{" "}
                <span className="font-semibold text-slate-700">Add provider</span>{" "}
                to create the first roster entry.
              </div>
            )}
          </div>
        </section>

        <section className="space-y-6">
          <article className="v11-section p-5">
            {!detail ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
                Select a provider to inspect credentialing detail.
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">
                      {detail.provider.name}
                    </h2>
                    <p className="mt-1 text-sm text-slate-600">
                      {detail.provider.specialty} • {activeFacilityName} • NPI{" "}
                      {detail.provider.npi} • Status {detail.provider.status}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditForm((open) => !open);
                        setFormError(null);
                        setEditName(detail.provider.name);
                        setEditNpi(detail.provider.npi);
                        setEditSpecialty(detail.provider.specialty);
                        setEditStatus(detail.provider.status);
                        setEditFacilityId(detail.provider.facility_id);
                      }}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                    >
                      <Pencil size={14} />
                      {showEditForm ? "Close edit" : "Edit provider"}
                    </button>
                    <span
                      className={`rounded-full px-3 py-1 text-sm font-bold ${EXPIRY_STYLES[detail.summary.provider_status]}`}
                    >
                      {detail.summary.provider_status}
                    </span>
                  </div>
                </div>

                {showEditForm && (
                  <form
                    className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4"
                    onSubmit={(event) => {
                      event.preventDefault();
                      setFormError(null);
                      updateProviderMutation.mutate();
                    }}
                  >
                    <p className="text-xs font-black uppercase tracking-widest text-slate-500">
                      Edit provider
                    </p>
                    <label className="block text-xs font-semibold text-slate-600">
                      Facility
                      <select
                        value={editFacilityId}
                        onChange={(event) => setEditFacilityId(event.target.value)}
                        className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800"
                      >
                        {(facilitiesQuery.data ?? []).map((facility) => (
                          <option key={facility.facility_id} value={facility.facility_id}>
                            {facility.facility_name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block text-xs font-semibold text-slate-600">
                      Name
                      <input
                        value={editName}
                        onChange={(event) => setEditName(event.target.value)}
                        className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800"
                        required
                      />
                    </label>
                    <label className="block text-xs font-semibold text-slate-600">
                      NPI (10 digits)
                      <input
                        value={editNpi}
                        onChange={(event) => setEditNpi(event.target.value)}
                        className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800"
                        inputMode="numeric"
                        pattern="\d{10}"
                        required
                      />
                    </label>
                    <label className="block text-xs font-semibold text-slate-600">
                      Specialty
                      <input
                        value={editSpecialty}
                        onChange={(event) => setEditSpecialty(event.target.value)}
                        className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800"
                        required
                      />
                    </label>
                    <label className="block text-xs font-semibold text-slate-600">
                      Status
                      <select
                        value={editStatus}
                        onChange={(event) => setEditStatus(event.target.value as ProviderStatus)}
                        className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800"
                      >
                        {PROVIDER_STATUS_OPTIONS.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="submit"
                      disabled={updateProviderMutation.isPending}
                      className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                    >
                      {updateProviderMutation.isPending ? "Saving…" : "Save provider"}
                    </button>
                  </form>
                )}

                <div className="grid gap-3 md:grid-cols-4">
                  <div className="v11-mini-metric text-left">
                    <p className="text-xs font-black uppercase tracking-[0.24em] text-slate-500">
                      Credentials
                    </p>
                    <p className="mt-2 text-3xl font-black text-slate-900">
                      {detail.credentials.length}
                    </p>
                  </div>
                  <div className="v11-mini-metric text-left">
                    <p className="text-xs font-black uppercase tracking-[0.24em] text-slate-500">
                      Privileges
                    </p>
                    <p className="mt-2 text-3xl font-black text-slate-900">
                      {detail.privileges.length}
                    </p>
                  </div>
                  <div className="v11-mini-metric text-left">
                    <p className="text-xs font-black uppercase tracking-[0.24em] text-slate-500">
                      Documents
                    </p>
                    <p className="mt-2 text-3xl font-black text-slate-900">
                      {documents.length}
                    </p>
                  </div>
                  <div className="v11-mini-metric text-left">
                    <p className="text-xs font-black uppercase tracking-[0.24em] text-slate-500">
                      Next Reappointment Trigger
                    </p>
                    <p className="mt-2 text-lg font-bold text-slate-900">
                      {formatDate(detail.summary.next_expiry_date)}
                    </p>
                  </div>
                </div>

                <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                  <div>
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-500">
                      Credentials
                    </h3>
                    <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200">
                      <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead className="bg-slate-50 text-left text-slate-500">
                          <tr>
                            <th className="px-4 py-3 font-semibold">Type</th>
                            <th className="px-4 py-3 font-semibold">Issuer</th>
                            <th className="px-4 py-3 font-semibold">Expiry</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {detail.credentials.map((credential) => (
                            <tr key={credential.credential_id}>
                              <td className="px-4 py-3 font-semibold text-slate-900">
                                {formatCredentialType(credential.credential_type)}
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {credential.issuer}
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {formatDate(credential.expiry_date)}
                              </td>
                            </tr>
                          ))}
                          {detail.credentials.length === 0 && (
                            <tr>
                              <td
                                colSpan={3}
                                className="px-4 py-4 text-sm text-slate-500"
                              >
                                No credentials recorded yet.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-500">
                      Privileges Matrix
                    </h3>
                    <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200">
                      <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead className="bg-slate-50 text-left text-slate-500">
                          <tr>
                            <th className="px-4 py-3 font-semibold">Category</th>
                            <th className="px-4 py-3 font-semibold">Granted</th>
                            <th className="px-4 py-3 font-semibold">Expiry</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {detail.privileges.map((privilege) => (
                            <tr key={privilege.privilege_id}>
                              <td className="px-4 py-3 font-semibold text-slate-900">
                                {formatCredentialType(privilege.procedure_category)}
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {formatDate(privilege.granted_date)}
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {formatDate(privilege.expiry_date)}
                              </td>
                            </tr>
                          ))}
                          {detail.privileges.length === 0 && (
                            <tr>
                              <td
                                colSpan={3}
                                className="px-4 py-4 text-sm text-slate-500"
                              >
                                No privileges recorded yet.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-black uppercase tracking-widest text-slate-500">
                    Reappointment Timeline
                  </h3>
                  <div className="mt-3 space-y-3">
                    {timeline.map((entry) => (
                      <div
                        key={entry.id}
                        className="v11-dashboard-card flex items-center justify-between px-4 py-3"
                      >
                        <div>
                          <p className="font-semibold text-slate-900">
                            {entry.label}
                          </p>
                          <p className="text-sm text-slate-500">{entry.kind}</p>
                        </div>
                        <p className="text-sm font-semibold text-slate-700">
                          {formatDate(entry.expiry_date)}
                        </p>
                      </div>
                    ))}
                    {timeline.length === 0 && (
                      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">
                        No scheduled expirations yet.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </article>

          <article className="v11-section p-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">
                  Credential Documents
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  Upload supporting credential files for the selected provider.
                  Sprint 3 stays bounded to metadata and storage only.
                </p>
              </div>
              <button
                type="button"
                onClick={() => alertMutation.mutate()}
                disabled={!activeProviderId || alertMutation.isPending}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <BellRing size={14} />
                {alertMutation.isPending ? "Sending…" : "Send Expiring Alerts"}
              </button>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-4">
              <select
                value={uploadType}
                onChange={(event) =>
                  setUploadType(event.target.value as CredentialDocumentType | "other")}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
              >
                <option value="medical_license">Medical License</option>
                <option value="dea_registration">DEA Registration</option>
                <option value="board_certification">Board Certification</option>
                <option value="malpractice_insurance">Malpractice Insurance</option>
                <option value="npdb_query">NPDB Query</option>
                <option value="oig_sam_check">OIG/SAM Check</option>
                <option value="bls">BLS</option>
                <option value="acls">ACLS</option>
                <option value="privilege_supporting">Privilege Supporting</option>
                <option value="other">Other</option>
              </select>
              <input
                value={uploadLabel}
                onChange={(event) => setUploadLabel(event.target.value)}
                placeholder="Document label"
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
              />
              <select
                value={uploadCredentialId}
                onChange={(event) => setUploadCredentialId(event.target.value)}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
              >
                <option value="">Link to credential (optional)</option>
                {(detail?.credentials ?? []).map((credential) => (
                  <option
                    key={credential.credential_id}
                    value={credential.credential_id}
                  >
                    {formatCredentialType(credential.credential_type)}
                  </option>
                ))}
              </select>
              <label
                htmlFor="credential-document-upload"
                className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700"
              >
                <Upload size={14} />
                <span>{selectedFile?.name ?? "Choose file"}</span>
              </label>
              <input
                id="credential-document-upload"
                aria-label="Choose file"
                type="file"
                className="hidden"
                onChange={(event) =>
                  setSelectedFile(event.target.files?.[0] ?? null)}
              />
            </div>
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={() => uploadMutation.mutate()}
                disabled={!selectedFile || uploadMutation.isPending}
                className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {uploadMutation.isPending ? "Uploading…" : "Upload Document"}
              </button>
            </div>

            <div className="mt-4 space-y-3">
              {documents.length === 0 && (
                <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500">
                  No credential documents uploaded yet.
                </p>
              )}
              {documents.map((document) => (
                <div
                  key={document.document_id}
                  className="rounded-2xl border border-slate-200 bg-white p-4"
                >
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <div className="text-sm font-bold text-slate-900">
                        {document.label}
                      </div>
                      <div className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-500">
                        {formatCredentialType(document.document_type)}
                      </div>
                    </div>
                    <div className="text-xs text-slate-500">
                      Uploaded {formatDate(document.created_at)}
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    {document.mime_type ?? "Unknown type"}
                    {document.file_size_bytes
                      ? ` • ${Math.round(document.file_size_bytes / 1024)} KB`
                      : ""}
                  </div>
                </div>
              ))}
            </div>
          </article>
        </section>
      </div>
    </div>
  );
}
